import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { ManageAccounts, OpenInNew, Share, EditOutlined, DeleteOutline } from '@mui/icons-material';
import { Search } from 'lucide-react';
import partnerAdminService from '../../services/partnerAdminService';
import resourceSharingService from '../../services/resourceSharingService';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import { principalLabel } from '../shared/sharingLabels';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { describeApiError } from '../../utils/apiError';
import { SHARING_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import PaginationBar from '../PlatformAdmin/PaginationBar';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const RESOURCE_TYPES = [
  { value: 'agent', label: 'Agents' },
  { value: 'folder_dim1', label: 'Rules book (D1)' },
  { value: 'folder_dim3', label: 'Customer Data (D3)' },
  { value: 'composio_app', label: 'Integrations' },
  { value: 'custom_tool', label: 'Custom tools' },
];
const PAGE_SIZE = 25;

const typeLabel = (type) => RESOURCE_TYPES.find((t) => t.value === type)?.label || type;
const resourceName = (item) =>
  item?.resource?.name ||
  item?.resource?.app_name ||
  item?.resource?.tool_key ||
  item?.resource_name ||
  `${typeLabel(item?.resource_type)} #${item?.resource_id}`;
const ownerName = (owner) =>
  owner?.name || [owner?.first_name, owner?.last_name].filter(Boolean).join(' ') || owner?.email || 'Unknown owner';
const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

/**
 * Cross-company sharing (Partner tier guide §5.6): browse what partner staff can
 * share — the partner's own resources and every client company's — and share it
 * across the partner's companies; plus the list of those cross-company shares.
 * Partner staff can view client resources read-only but not edit them; sharing
 * a client's resource is visible to (and revocable by) that client's admins.
 */
const PartnerSharingPanel = ({ ctx, notify }) => {
  const { companies, homeCompanyId, partnerName } = ctx;
  const [view, setView] = useState('resources');

  // Resource browser
  const [resourceType, setResourceType] = useState('agent');
  const [ownerCompany, setOwnerCompany] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareResource, setShareResource] = useState(null);
  const [manageResource, setManageResource] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  // Cross-company shares
  const [sharesType, setSharesType] = useState('');
  const [sharesOffset, setSharesOffset] = useState(0);
  const [shares, setShares] = useState([]);
  const [sharesTotal, setSharesTotal] = useState(0);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [sharesError, setSharesError] = useState('');
  const [editTarget, setEditTarget] = useState(null); // { share, resource }
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const isHome = (companyId) => homeCompanyId != null && Number(companyId) === Number(homeCompanyId);
  const ownerCompanyLabel = (companyId) =>
    isHome(companyId) ? TENANCY_COPY.ownedByYou(partnerName) : ctx.companyLabel(companyId);
  const ownerCompanyNameForDialog = (companyId) => (isHome(companyId) ? partnerName : ctx.companyLabel(companyId));

  const loadResources = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await partnerAdminService.listResources({
        resourceType,
        companyId: ownerCompany || null,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setError(describeApiError(err, 'Could not load resources.'));
    } finally {
      setLoading(false);
    }
  }, [resourceType, ownerCompany, debouncedSearch, offset]);

  const loadShares = useCallback(async () => {
    setSharesLoading(true);
    setSharesError('');
    try {
      const data = await partnerAdminService.listShares({
        resourceType: sharesType || null,
        limit: PAGE_SIZE,
        offset: sharesOffset,
      });
      const list = Array.isArray(data?.shares) ? data.shares : [];
      // One row per top-level share (children travel with their agent).
      setShares(list.filter((s) => !s.parent_share_id));
      setSharesTotal(Number(data?.total) || list.length);
    } catch (err) {
      setSharesError(describeApiError(err, 'Could not load cross-company shares.'));
    } finally {
      setSharesLoading(false);
    }
  }, [sharesType, sharesOffset]);

  useEffect(() => {
    if (view === 'resources') loadResources();
  }, [view, loadResources]);

  useEffect(() => {
    if (view === 'shares') loadShares();
  }, [view, loadShares]);

  const dialogResource = (item) => ({
    type: item.resource_type,
    id: item.resource_id,
    name: resourceName(item),
    ownerCompanyId: item.owner_company_id ?? null,
    ownerCompanyName: ownerCompanyNameForDialog(item.owner_company_id),
  });

  const onShared = () => {
    // Partner-mode warnings were already shown inside the dialog.
    notify('Shared across companies.', 'success');
    loadResources();
  };

  const openEdit = (share) => {
    const ownerId = share.owner_company_id ?? share.company_id ?? null;
    setEditTarget({
      share,
      resource: {
        type: share.resource_type,
        id: share.resource_id,
        name: resourceName(share),
        ownerCompanyId: ownerId,
        ownerCompanyName: ownerId != null ? ownerCompanyNameForDialog(ownerId) : undefined,
      },
    });
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      setBusy(true);
      await resourceSharingService.revokeShare(revokeTarget.id);
      notify('Share revoked. People in those companies lose access immediately.', 'info');
      setRevokeTarget(null);
      await loadShares();
    } catch (err) {
      notify(describeApiError(err, 'Could not revoke the share.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        className={styles.tabs}
        TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
      >
        <Tab value="resources" label="Resources" className={styles.tab} />
        <Tab value="shares" label="Your cross-company shares" className={styles.tab} />
      </Tabs>

      {view === 'resources' ? (
        <>
          <Box className={styles.toolbar}>
            <Box className={styles.filterGroup}>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Resource type</InputLabel>
                <Select label="Resource type" value={resourceType} onChange={(e) => { setResourceType(e.target.value); setOffset(0); }}>
                  {RESOURCE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Owner company</InputLabel>
                <Select label="Owner company" value={ownerCompany} onChange={(e) => { setOwnerCompany(e.target.value); setOffset(0); }}>
                  <MenuItem value="">All companies</MenuItem>
                  {homeCompanyId != null && (
                    <MenuItem value={homeCompanyId}>{TENANCY_COPY.ownedByYou(partnerName)}</MenuItem>
                  )}
                  {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Search resources…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
                sx={{ minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TableContainer className={styles.tableCard}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Sharing</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 8, color: 'text.secondary' }}>No resources match these filters.</TableCell></TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={`${item.resource_type}-${item.resource_id}`} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{resourceName(item)}</Typography>
                        <Typography variant="caption" color="text.secondary">{typeLabel(item.resource_type)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ opacity: item.owner?.is_active === false ? 0.55 : 1 }}>
                          {ownerName(item.owner)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ownerCompanyLabel(item.owner_company_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`${item.share_count || 0} in-company`} />
                          <Chip
                            size="small"
                            color={item.partner_share_count ? 'primary' : 'default'}
                            variant={item.partner_share_count ? 'outlined' : 'filled'}
                            label={`${item.partner_share_count || 0} across companies`}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" startIcon={<OpenInNew />} onClick={() => setViewItem(item)}>View</Button>
                        <Button size="small" startIcon={<Share />} onClick={() => setShareResource(dialogResource(item))}>
                          {SHARING_COPY.shareAcross}
                        </Button>
                        <Button size="small" startIcon={<ManageAccounts />} onClick={() => setManageResource(dialogResource(item))}>
                          Who has access
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 0 && (
            <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="resources" onOffsetChange={setOffset} />
          )}
        </>
      ) : (
        <>
          <Box className={styles.toolbar}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Resource type</InputLabel>
              <Select label="Resource type" value={sharesType} onChange={(e) => { setSharesType(e.target.value); setSharesOffset(0); }}>
                <MenuItem value="">All types</MenuItem>
                {RESOURCE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {sharesError && <Alert severity="error">{sharesError}</Alert>}

          <TableContainer className={styles.tableCard}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell>Shared with</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sharesLoading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : shares.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8, color: 'text.secondary' }}>Nothing is shared across companies yet.</TableCell></TableRow>
                ) : (
                  shares.map((share) => (
                    <TableRow key={share.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{resourceName(share)}</Typography>
                        <Typography variant="caption" color="text.secondary">{typeLabel(share.resource_type)}</Typography>
                      </TableCell>
                      <TableCell>{principalLabel(share, { partnerName, homeCompanyId })}</TableCell>
                      <TableCell>
                        {share.resource_type === 'composio_app'
                          ? (share.options?.auth_mode || 'with_auth') === 'with_auth' ? "Owner's connection" : 'Each person connects'
                          : (share.permission || 'read') === 'write' ? 'Write' : 'Read'}
                      </TableCell>
                      <TableCell>{formatDate(share.created_at)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" startIcon={<EditOutlined />} onClick={() => openEdit(share)}>Edit</Button>
                        <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => setRevokeTarget(share)}>Revoke</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {sharesTotal > 0 && (
            <PaginationBar total={sharesTotal} limit={PAGE_SIZE} offset={sharesOffset} loading={sharesLoading} unit="shares" onOffsetChange={setSharesOffset} />
          )}
        </>
      )}

      <ResourceShareDialog
        open={Boolean(shareResource)}
        resource={shareResource}
        defaultScope="partner"
        onClose={() => setShareResource(null)}
        onSuccess={onShared}
      />
      <ManageSharingDialog
        open={Boolean(manageResource)}
        resource={manageResource}
        onClose={() => setManageResource(null)}
        onChanged={loadResources}
      />
      <ResourceShareDialog
        open={Boolean(editTarget)}
        resource={editTarget?.resource}
        initialShare={editTarget?.share || null}
        onClose={() => setEditTarget(null)}
        onSuccess={() => { notify('Share updated.', 'success'); loadShares(); }}
      />

      {/* Read-only view (partner staff can see, not edit, client resources) */}
      <Dialog open={Boolean(viewItem)} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Resource details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ fontWeight: 750 }}>{viewItem ? resourceName(viewItem) : ''}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Owned by {ownerName(viewItem?.owner)} {viewItem?.owner?.email ? `(${viewItem.owner.email})` : ''} ·{' '}
            {viewItem ? ownerCompanyLabel(viewItem.owner_company_id) : ''}
          </Typography>
          {viewItem && !isHome(viewItem.owner_company_id) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {SHARING_COPY.clientOwnedBanner(ctx.companyLabel(viewItem.owner_company_id))}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gap: 1 }}>
            {Object.entries(viewItem?.resource || {})
              .filter(([key]) => !['id', 'name', 'app_name'].includes(key))
              .map(([key, value]) => (
                <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{key.replaceAll('_', ' ')}</Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {value === null || value === undefined || value === '' ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </Typography>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setViewItem(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Revoke a cross-company share */}
      <Dialog open={Boolean(revokeTarget)} onClose={() => !busy && setRevokeTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Revoke this share?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2">
            {revokeTarget && `${resourceName(revokeTarget)} will stop being shared with ${principalLabel(revokeTarget, { partnerName, homeCompanyId })}. People there lose access immediately.`}
          </Typography>
          {revokeTarget?.resource_type === 'agent' && (
            <Alert severity="warning">This also removes access to folders and tools shared with this agent.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={busy}>Keep</Button>
          <Button color="error" variant="contained" onClick={revoke} disabled={busy}>
            {busy ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerSharingPanel;
